import "./App.css";
import { BrowserRouter, HashRouter, Navigate, Route, Routes } from "react-router-dom";
import Layout from "./components/Layout";
import EventRegistration from "./components/company/events/EventRegistration";
import Dashboard from "./components/DashboardRouter";
import { useContext, useEffect } from "react";
import AppContext from "./AppContext";
import Login from "./components/auth/Login";
import Company from "./components/company/CompanyListPage";
import CompanyForm from "./components/company/CompanyForm";
import User from "./components/user/UserListPage";
import UserForm from "./components/user/UserForm";
import EmployeeList from "./components/employee/EmployeeListPage";
import EmployeeForm from "./components/employee/EmployeeForm";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import Subscription from "./components/subscription/SubscriptionListPage";
import SubscriptionForm from "./components/subscription/SubscriptionForm";
import Settings from "./components/settings/CompanySettingsPage";
import { adjustShade } from "./utls/adjustShade";
import PublicEmployeeCard from "./components/shared/PublicEmployeeCard";
import ChangePassword from "./components/auth/ChangePassword";
import SignupPage from "./components/SignUpPage";
import EmployeeRegistrationForm from "./components/publicEmployees/EmployeeRegistration";
import { PublicEmployeesList } from "./components/publicEmployees/EmployeesList";
import PublicEmployeeEditForm from "./components/publicEmployees/PublicEmployeeEditForm";
import SubscriptionDetails from "./components/publicEmployees/SubscriptionDetails";
import BusinessCardScanner from "./components/publicEmployees/BusinessCardScanner";
import { RoleEnum } from "./Constants";
import CouponsList from "./components/coupons/CouponsList";
import CouponForm from "./components/coupons/CouponForm";
import CouponDetails from "./components/coupons/CouponDetails";
import CatalogManagementPage from "./components/catalog/CatalogManagementPage";
import EmployeeDetails from "./components/publicEmployees/EmployeeDetails";
import PaymentSuccess from "./components/publicEmployees/PaymentSuccess";
import TransactionHistory from "./components/publicEmployees/TransactionHistory";
import ProtectedRoute from "./components/ProtectedRoute";
import ThemeCustomizer from "./components/shared/ThemeCustomizer";
import CardCustomizer from "./components/shared/CardCustomizer";
import { renderIndividualChildRoutes } from "./individualRoutes";
import EventManagementPage from "./components/events/EventManagementPage";
import EventFormPage from "./components/events/EventFormPage";
import EventRegistrationsPage from "./components/events/EventRegistrationsPage";
import EventOrganizersPage from "./components/events/EventOrganizersPage";
import EventAnalyticsPage from "./components/events/EventAnalyticsPage";
import QRCheckInPage from "./components/events/QRCheckInPage";
import EventScannerPage from "./components/events/EventScannerPage";
import MyPackagesPage from "./components/subscription/MyPackagesPage";
import MyProfilePage from "./components/user/MyProfilePage";
// COMMENTED OUT: Use event-landing mobile app for organizer scanning instead
// import OrganizerScannerPage from "./components/events/OrganizerScannerPage";
function App() {
  const { isLoggedIn, primaryColor, profile, hasEventAccess, isVirtual } = useContext(AppContext);
  useEffect(() => {
    const shades = {
      50: adjustShade(primaryColor, 40), // Lighten a lot
      100: adjustShade(primaryColor, 30),
      200: adjustShade(primaryColor, 20),
      300: adjustShade(primaryColor, 10),
      400: adjustShade(primaryColor, 5),
      500: primaryColor, // Base color
      600: adjustShade(primaryColor, -5),
      700: adjustShade(primaryColor, -10),
      800: adjustShade(primaryColor, -20),
      900: adjustShade(primaryColor, -30), // Darken a lot
    };

    Object.entries(shades).forEach(([shade, color]) => {
      document.documentElement.style.setProperty(`--indigo-${shade}`, color);
    });
  }, [primaryColor]);

  return (
    <>
      <BrowserRouter>
        {isLoggedIn ? (
          profile.role !== RoleEnum.PUBLIC_EMPLOYEE ? (
            <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/companies" element={isVirtual ? <Navigate to="/events" replace /> : <Company />} />
                <Route path="/company-form/:id?" element={isVirtual ? <Navigate to="/events" replace /> : <CompanyForm />} />
                <Route path="/users" element={<User />} />
                <Route path="/user-form/:id?" element={<UserForm />} />
                <Route path="/employees" element={isVirtual ? <Navigate to="/events" replace /> : <EmployeeList />} />
                <Route
                  path="/publicEmployees"
                  element={<PublicEmployeesList />}
                />
                <Route
                  path="/public-employee-form/:id?"
                  element={<PublicEmployeeEditForm />}
                >
                  <Route index element={<EmployeeDetails />} />
                  <Route path="details" element={<EmployeeDetails />} />
                  <Route path="theme" element={<ThemeCustomizer />} />
                  <Route path="customize-card" element={<CardCustomizer />} />
                  <Route
                    path="subscription"
                    element={<SubscriptionDetails />}
                  />
                  <Route
                    path="transaction-history"
                    element={<TransactionHistory />}
                  />
                  <Route
                    path="business-cards"
                    element={<BusinessCardScanner />}
                  />
                 
                </Route>
                <Route
                  path="/subscription-details/:id?"
                  element={<SubscriptionDetails />}
                />
                <Route path="/settings/:id" element={isVirtual ? <Navigate to="/events" replace /> : <Settings />} />
                <Route path="/employee-form/:id?" element={isVirtual ? <Navigate to="/events" replace /> : <EmployeeForm />} />
                <Route
                  path="/employee-id/:id"
                  element={<PublicEmployeeCard cardType={"company"} />}
                />
                <Route
                  path="/public-employee/:token"
                  element={<PublicEmployeeCard cardType={"person"} />}
                />
                <Route path="/subscriptions" element={<Subscription />} />
                <Route
                  path="/subscription-form/:id?"
                  element={<SubscriptionForm />}
                />{" "}
                <Route path="/coupons" element={<CouponsList />} />
                <Route path="/coupon-form/:id?" element={<CouponForm />} />
                <Route path="/coupon-details/:id" element={<CouponDetails />} />
                <Route path="/catalog" element={<CatalogManagementPage />} />
                <Route path="/events" element={hasEventAccess ? <EventManagementPage /> : <Navigate to="/" replace />} />
                <Route path="/event-form/:id?" element={hasEventAccess ? <EventFormPage /> : <Navigate to="/" replace />} />
                <Route path="/event-registrations/:id" element={hasEventAccess ? <EventRegistrationsPage /> : <Navigate to="/" replace />} />
                <Route path="/event-organizers/:id" element={hasEventAccess ? <EventOrganizersPage /> : <Navigate to="/" replace />} />
                <Route path="/dashboard/events/:id/analytics" element={hasEventAccess ? <EventAnalyticsPage /> : <Navigate to="/" replace />} />
                <Route path="/event-scanner/:eventId" element={hasEventAccess ? <EventScannerPage /> : <Navigate to="/" replace />} />
                <Route path="/my-packages" element={isVirtual ? <MyPackagesPage /> : <Navigate to="/" replace />} />
                <Route path="/my-profile" element={isVirtual ? <MyProfilePage /> : <Navigate to="/" replace />} />
                <Route path="/subscriptionSuccess" element={<PaymentSuccess />} />
                <Route path="/register-event/:eventName" element={<EventRegistration />} />
                {/* COMMENTED OUT: Use event-landing mobile app for organizer scanning instead */}
                {/* <Route path="/organizer-scanner/:eventId" element={<OrganizerScannerPage />} /> */}
                <Route
                  path="/registration"
                  element={<EmployeeRegistrationForm />}
                />
              </Routes>
            </Layout>
          ) : (
            <Layout>
              <Routes>
                <Route path="/" element={<PublicEmployeeEditForm />}>
                  {renderIndividualChildRoutes()}
                </Route>
                <Route
                  path="/employee-id/:id"
                  element={<PublicEmployeeCard cardType={"company"} />}
                />
                <Route
                  path="/public-employee/:token"
                  element={<PublicEmployeeCard cardType={"person"} />}
                />
                <Route
                  path="/registration"
                  element={<EmployeeRegistrationForm />}
                />
              </Routes>
            </Layout>
          )
        ) : (
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/changePassword/:token" element={<ChangePassword />} />
            <Route path="/complete-signup" element={<SignupPage />} />
            <Route
              path="/registration"
              element={<EmployeeRegistrationForm />}
            />
            <Route
              path="/employee-id/:id"
              element={<PublicEmployeeCard cardType={"company"} />}
            />
            <Route
              path="/public-employee/:token"
              element={<PublicEmployeeCard cardType={"person"} />}
            />
            <Route path="/event-check-in" element={<QRCheckInPage />} />
            {/* Commercial Individual Digital Card: after ClickPay the account exists but the
                user is not yet signed in, so the payment result page must be reachable
                unauthenticated (matches origin/development). "Continue to Login" then takes
                over. */}
            <Route path="/subscriptionSuccess" element={<PaymentSuccess />} />

            {/* Catch-all route for handling email redirects when not logged in */}
            <Route path="*" element={<ProtectedRoute />} />
          </Routes>
        )}{" "}
      </BrowserRouter>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
}

export default App;
